import { Link } from "react-router-dom";
import { Facebook, Instagram, Linkedin, Mail, MapPin, Phone, Twitter, Youtube, Clock } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { Logo } from "@/components/site/Logo";
import { useMenu } from "@/context/SiteContext";

const SOCIAL = { facebook: Facebook, instagram: Instagram, linkedin: Linkedin, youtube: Youtube, twitter: Twitter };

const Col = ({ title, children }) => (
  <div>
    <h3 className="mb-4 font-heading text-sm font-bold uppercase tracking-[0.14em] text-white">{title}</h3>
    <ul className="space-y-2.5 text-sm">{children}</ul>
  </div>
);

const FLink = ({ to, children, testId }) => (
  <li><Link to={to} className="text-white/65 transition-colors hover:text-cta" data-testid={testId}>{children}</Link></li>
);

export const Footer = () => {
  const { settings: s, legal } = useSite();
  const year = new Date().getFullYear();
  const menu = useMenu("footer");
  const openCookies = () => window.dispatchEvent(new Event("obt-open-cookie-settings"));

  return (
    <footer className="bg-navy text-white/70" data-testid="site-footer">
      <div className="container-x grid gap-12 py-16 md:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1.2fr_1.3fr]">
        <div>
          <Logo light testId="footer-logo" />
          <p className="mt-5 max-w-xs text-sm leading-relaxed text-white/65">Custom software, mobile apps, CRMs and AI automation built for Indian businesses.</p>
          <div className="mt-6 flex gap-2.5">
            {Object.entries(s?.social_links || {}).filter(([, v]) => v).map(([k, v]) => {
              const Icon = SOCIAL[k];
              return Icon ? (
                <a key={k} href={v} target="_blank" rel="noopener noreferrer" aria-label={k} className="grid h-10 w-10 place-items-center rounded-xl border border-white/10 bg-white/5 transition-colors hover:border-cta hover:text-cta" data-testid={`footer-social-${k}`}>
                  <Icon className="h-4 w-4" />
                </a>
              ) : null;
            })}
          </div>
        </div>
        <Col title="Quick Links">
          {menu.map((n) => <FLink key={n.to} to={n.to} testId={`footer-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>{n.label}</FLink>)}
        </Col>
        <Col title="Legal">
          {legal.map((l) => <FLink key={l.slug} to={`/${l.slug}`} testId={`footer-legal-${l.slug}`}>{l.title}</FLink>)}
          <li><button onClick={openCookies} className="text-white/65 transition-colors hover:text-cta" data-testid="footer-cookie-settings-btn">Cookie Settings</button></li>
        </Col>
        <Col title="Contact">
          <li className="flex gap-2.5"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-sky" /><span>{s?.address}</span></li>
          <li><a href={`tel:${s?.phone?.replace(/\s/g, "")}`} className="flex gap-2.5 hover:text-cta" data-testid="footer-phone"><Phone className="h-4 w-4 text-sky" />{s?.phone}</a></li>
          <li><a href={`mailto:${s?.email}`} className="flex gap-2.5 hover:text-cta" data-testid="footer-email"><Mail className="h-4 w-4 text-sky" />{s?.email}</a></li>
          <li className="flex gap-2.5"><Clock className="mt-0.5 h-4 w-4 shrink-0 text-sky" />{s?.support_hours}</li>
        </Col>
      </div>
      <div className="border-t border-white/10">
        <div className="container-x py-6 text-xs leading-relaxed text-white/55" data-testid="footer-business-info">
          <p>
            <span className="font-semibold text-white/80">Business Information:</span> {s?.legal_name} ({s?.business_type}) · {s?.address} · {s?.email} · {s?.phone}
            {s?.gstin ? <> · GSTIN: {s.gstin}</> : null}
          </p>
          <p className="mt-2" data-testid="footer-copyright">© {year} {s?.legal_name || "Own Brand Technologies"}. All rights reserved. Payments secured by Razorpay.</p>
        </div>
      </div>
    </footer>
  );
};
